import { useState, useEffect, useRef } from 'react'
import type { PointerEvent } from 'react'
import { NavLink } from 'react-router-dom'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useAppStore } from '@/store'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useIsEmpresaAll } from '@/hooks/useIsEmpresaAll'
import { useModulos } from '@/hooks/useModulos'
import { useSinProduccion } from '@/hooks/useSinProduccion'
import { useHasBomAccess } from '@/hooks/useHasBomAccess'
import { useHasReportsAccess } from '@/hooks/useHasReportsAccess'
import { HelpCenterPanel } from '@/components/help/HelpCenterPanel'
import { useBrand } from '@/branding/useBrand'
import { seccionesVisibles } from '@/utils/navSecciones'

// ── SVG icons ─────────────────────────────────────────────────────────────────

const IconHome = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 6.5L8 2l6 4.5V14a1 1 0 01-1 1H3a1 1 0 01-1-1V6.5z"/>
    <path d="M6 15v-5h4v5"/>
  </svg>
)

const IconWeek = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <rect x="2" y="2" width="12" height="12" rx="1.5"/>
    <path d="M2 5.5h12M5 2v3.5M11 2v3.5"/>
    <path d="M4.5 8.5h2.5M9 8.5h2.5M4.5 11h2.5M9 11h2.5"/>
  </svg>
)

const IconPlanGrid = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="12" height="12" rx="1.5"/>
    <path d="M2 6h12M2 10h12M6.5 2v12"/>
  </svg>
)

const IconSequence = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4h12M2 8h12M2 12h8" /><circle cx="13.5" cy="12" r="1.5" />
  </svg>
)

const IconInventory = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4l6-2 6 2v8l-6 2-6-2V4z"/>
    <path d="M8 2v12M2 4l6 2 6-2"/>
  </svg>
)

const IconPedidosCompra = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h12l-1 7H3L2 3z"/>
    <path d="M5 3V2a1 1 0 011-1h4a1 1 0 011 1v1"/>
    <circle cx="6" cy="13.5" r="1"/><circle cx="11" cy="13.5" r="1"/>
    <path d="M6 7h4M8 5v4"/>
  </svg>
)

const IconReports = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 1.5h7l3 3V14a.5.5 0 01-.5.5h-9A.5.5 0 013 14V2a.5.5 0 010-.5z"/>
    <path d="M9.5 1.5v3.5H13"/>
    <path d="M5.5 11.5V9M8 11.5V7.5M10.5 11.5v-2"/>
  </svg>
)

const IconPedidosVenta = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 3h14M3 3l1 10h8l1-10"/>
    <path d="M6 7h4M7 10h2"/>
    <path d="M10 3V2a1 1 0 00-1-1H7a1 1 0 00-1 1v1"/>
  </svg>
)

const IconProduccion = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="9" width="3" height="5" rx="0.5"/>
    <rect x="6.5" y="5" width="3" height="9" rx="0.5"/>
    <rect x="11" y="2" width="3" height="12" rx="0.5"/>
    <polyline points="2,7 5.5,4 9,6 14,1"/>
  </svg>
)

const IconDatasheet = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 1.5h5L13 5v9.5a.5.5 0 01-.5.5h-9a.5.5 0 01-.5-.5V2a.5.5 0 01.5-.5z"/>
    <path d="M9 1.5V5h4M5.5 8.5h5M5.5 11h3"/>
  </svg>
)

const IconOutboundLoads = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1.5 10.5V4a1 1 0 011-1h6v7.5"/>
    <path d="M8.5 6.5h3l2 2.5v1.5h-5"/>
    <circle cx="4" cy="12" r="1.5"/><circle cx="11.5" cy="12" r="1.5"/>
    <path d="M1.5 8.5h3"/>
  </svg>
)

const IconDockSchedule = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="2.5" width="13" height="11" rx="1.5"/>
    <path d="M1.5 6h13"/>
    <path d="M5 6v7.5M10 6v7.5"/>
    <path d="M2.5 8.5h2M6 10.5h3M11 8.5h2.5"/>
  </svg>
)

const IconHelp = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="6.25"/>
    <path d="M6.2 6.1a1.85 1.85 0 113.4 1c-.5.7-1.2.9-1.5 1.5-.1.2-.1.4-.1.7"/>
    <circle cx="8" cy="11.6" r="0.3" fill="currentColor"/>
  </svg>
)

const IconLogout = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3M10.5 11l3-3-3-3M13.5 8H6"/>
  </svg>
)

const IconChevronLeft = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11L5 7l4-4"/>
  </svg>
)

const IconChevronRight = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 11l4-4-4-4"/>
  </svg>
)

const IconClose = () => (
  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M3 3l10 10M13 3L3 13"/>
  </svg>
)

const LANGS = ['es', 'en'] as const

interface EntradaMenu {
  to: string
  label: string
  Icon: () => JSX.Element
}

const ROTULO_SECCION: Record<string, string> = {
  planificacion: 'nav.sectionPlanning',
  operaciones:   'nav.sectionOperations',
  pedidos:       'nav.sectionOrders',
  logistica:     'nav.sectionLogistics',
  regulatory:    'nav.sectionRegulatory',
  reports:       'nav.sectionReports',
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Sidebar() {
  const { user, logout } = useAuth()
  const { sidebarCollapsed: collapsed, setSidebarCollapsed, dark, setDark } = useAppStore()
  const { isMobile } = useBreakpoint()
  const { t, i18n } = useTranslation()
  const isEmpresaAll = useIsEmpresaAll()
  // Quien sólo compra y vende no ve fabricación. Ver `useSinProduccion`.
  const sinProduccion = useSinProduccion()
  // Qué módulos abre este usuario. Esconder una entrada es CORTESÍA:
  // evita entrar a una pantalla que daría 403. El cierre real está en
  // `exigir_modulo`, en el backend.
  const { puede } = useModulos()
  const hasReports = useHasReportsAccess()
  const hasBom = useHasBomAccess()
  const brand = useBrand()

  // State for language dropdown (only used in collapsed desktop mode)
  const [langOpen, setLangOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)

  // Close dropdown when sidebar expands/collapses or switches to mobile
  useEffect(() => { setLangOpen(false) }, [collapsed, isMobile])

  // Colapsado en escritorio, el menú se abre al pasar el ratón y se cierra al
  // salir. Se abre ENCIMA del contenido, no lo empuja: si cambiara el ancho de
  // <main>, las tablas anchas se recolocarían cada vez que el ratón cruza el
  // borde. Los retardos evitan que se abra al pasar de largo hacia la página.
  // Sólo ratón: en táctil el «hover» llega con el toque y abriría el menú al
  // pulsar un icono.
  const [hovered, setHovered] = useState(false)
  const hoverTimer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(hoverTimer.current), [])
  const programarHover = (v: boolean, ms: number) => {
    clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => setHovered(v), ms)
  }
  const onPointerEnter = (e: PointerEvent) => { if (e.pointerType === 'mouse') programarHover(true, 120) }
  const onPointerLeave = (e: PointerEvent) => { if (e.pointerType === 'mouse') programarHover(false, 250) }

  const flotante = !isMobile && collapsed && hovered
  const close = () => setSidebarCollapsed(true)
  const showLabels = isMobile || !collapsed || hovered

  // El menú, declarado. Cada entrada lleva su condición de acceso; el rótulo
  // de la sección NO lleva ninguna, porque sale solo cuando alguna entrada se ve.
  const secciones = seccionesVisibles<EntradaMenu>([
    {
      clave: 'planificacion',
      entradas: [
        { visible: !sinProduccion && puede('produccion'), entrada: { to: '/gantt', label: 'nav.advancedView', Icon: IconWeek } },
        // Secuencia: visible a todos los roles; el AVP entra a la vista restringida (8 columnas)
        { visible: !sinProduccion && puede('produccion'), entrada: { to: '/secuencia-produccion', label: 'nav.sequence', Icon: IconSequence } },
        { visible: !sinProduccion && puede('produccion') && isEmpresaAll, entrada: { to: '/plan-produccion', label: 'nav.planFormato', Icon: IconPlanGrid } },
      ],
    },
    {
      clave: 'operaciones',
      entradas: [
        { visible: puede('inventario'), entrada: { to: '/inventario', label: 'nav.inventory', Icon: IconInventory } },
        { visible: !sinProduccion && puede('produccion'), entrada: { to: '/produccion-diaria', label: 'nav.production', Icon: IconProduccion } },
        // Fichas técnicas: quien tenga acceso a tdis o a avp. Las empresas que
        // sólo compran y venden quedan fuera por ahí, sin condición aparte:
        // ninguna de ellas tiene tdis ni avp.
        { visible: hasBom && puede('bom'), entrada: { to: '/bom', label: 'nav.bom', Icon: IconDatasheet } },
      ],
    },
    {
      clave: 'pedidos',
      entradas: [
        { visible: puede('ventas'), entrada: { to: '/pedidos-ventas', label: 'nav.salesOrders', Icon: IconPedidosVenta } },
        // Situación de ventas: en pruebas, sólo en local (ver VentasSituacionPage).
        { visible: import.meta.env.DEV && puede('ventas'), entrada: { to: '/ventas-situacion', label: 'nav.salesStatus', Icon: IconPedidosVenta } },
        { visible: puede('compras'), entrada: { to: '/pedidos-compra', label: 'nav.purchaseOrders', Icon: IconPedidosCompra } },
      ],
    },
    {
      // Logística — qué sale (cargas de salida) y por dónde (cuadro de
      // muelles). Va por empresa, como pedidos: expedir es parte de la
      // operativa de cualquiera que venda.
      clave: 'logistica',
      entradas: [
        { visible: puede('logistica'), entrada: { to: '/cargas-salida', label: 'nav.outboundLoads', Icon: IconOutboundLoads } },
        { visible: puede('muelles'), entrada: { to: '/muelles', label: 'nav.dockSchedule', Icon: IconDockSchedule } },
      ],
    },
    {
      // Regulatory: el módulo nace sin pantallas. Su portada es /home
      // (`InicioPage`), así que hasta que tenga la primera la sección no sale.
      clave: 'regulatory',
      entradas: [],
    },
    {
      // Reports de cliente: solo para quien tiene acceso a la empresa avp
      clave: 'reports',
      entradas: [
        { visible: hasReports && puede('reports'), entrada: { to: '/reports', label: 'nav.reports', Icon: IconReports } },
      ],
    },
  ])

  const linkCls = ({ isActive }: { isActive: boolean }) =>
    clsx(
      'flex items-center gap-2.5 rounded-lg text-[13px] transition-colors',
      isMobile ? 'py-2.5' : 'py-2',
      // En escritorio el padding NO cambia al abrir: 18px centra el icono en
      // los 64px plegados, así que el icono se queda quieto y sólo aparece el texto.
      isMobile ? 'px-3' : 'px-[18px]',
      isActive
        ? 'bg-white/15 text-white font-semibold'
        : 'text-white/65 hover:bg-white/8 hover:text-white/90 active:bg-white/12',
    )

  function SectionLabel({ label }: { label: string }) {
    if (!showLabels) return null
    return (
      <div className={clsx('sidebar-label pt-3 pb-1 text-[9px] font-bold uppercase tracking-widest text-white/28 select-none', isMobile ? 'px-3' : 'px-[18px]')}>
        {label}
      </div>
    )
  }

  function SectionDivider() {
    return <div className="mx-2 my-1.5 border-t border-white/8" />
  }

  const aside = (
    <aside
      className={clsx(
        'flex flex-col bg-brand-dark text-white/90 overflow-hidden',
        isMobile
          ? clsx(
              'fixed inset-y-0 left-0 z-50 w-[260px] rounded-r-2xl shadow-2xl shadow-black/40 ring-1 ring-white/5',
              'transition-transform duration-300 ease-out will-change-transform motion-reduce:transition-none',
              collapsed ? '-translate-x-full' : 'translate-x-0',
            )
          : clsx(
              'absolute inset-y-0 left-0 z-50 whitespace-nowrap transition-[width,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
              showLabels ? 'w-[200px]' : 'w-[64px]',
              flotante && 'shadow-2xl shadow-black/40',
            ),
      )}
      style={{ height: '100vh' }}
      onPointerEnter={isMobile ? undefined : onPointerEnter}
      onPointerLeave={isMobile ? undefined : onPointerLeave}
    >
      {/* ── Logo ─────────────────────────────────────────────────────────── */}
      <div className={clsx(
        'flex items-center border-b border-white/10 py-5',
        isMobile ? 'px-4 justify-between' : !showLabels ? 'justify-center px-1.5' : 'gap-2 px-4',
      )}>
        <div className="flex items-center gap-2">
          <img
            src={brand.logoOnDark}
            alt={brand.name}
            className={clsx('shrink-0 object-contain', showLabels ? 'h-7 w-auto' : 'w-12 h-auto')}
            style={brand.invertLogoOnDark ? { filter: 'invert(1)' } : undefined}
          />
          {showLabels && (
            <div className="sidebar-label leading-tight min-w-0">
              <div className="text-[11px] font-semibold tracking-wide text-white">{brand.chromeLabel}</div>
              <div className="text-[10px] text-white/50">Factory</div>
            </div>
          )}
        </div>
        {isMobile && (
          <button
            onClick={close}
            className="text-white/50 hover:text-white transition-colors p-1"
            aria-label={t('nav.closeMenu')}
          >
            <IconClose />
          </button>
        )}
      </div>

      {/* ── Navigation ───────────────────────────────────────────────────── */}
      <nav className={clsx('flex-1 py-2 flex flex-col gap-0 overflow-y-auto overflow-x-hidden scroll-thin', isMobile ? 'px-2' : 'px-1.5')}>

        {/* Inicio */}
        <NavLink
          to="/home"
          className={linkCls}
          title={!showLabels ? t('nav.home') : undefined}
          onClick={isMobile ? close : undefined}
        >
          <IconHome />
          {showLabels && <span className="sidebar-label">{t('nav.home')}</span>}
        </NavLink>

        {/* Secciones: cada una sale sólo si tiene alguna entrada visible
            (ver `seccionesVisibles`). El separador va ANTES de cada sección,
            así que tampoco queda uno huérfano. */}
        {secciones.map(s => (
          <div key={s.clave}>
            <SectionDivider />
            <SectionLabel label={t(ROTULO_SECCION[s.clave])} />
            {s.entradas.map(e => (
              <NavLink
                key={e.to}
                to={e.to}
                className={linkCls}
                title={!showLabels ? t(e.label) : undefined}
                onClick={isMobile ? close : undefined}
              >
                <e.Icon />
                {showLabels && <span className="sidebar-label">{t(e.label)}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* ── Footer: Live · User · Actions · Language ─────────────────────── */}
      <div className={clsx('border-t border-white/10 py-3', !showLabels ? 'px-1.5' : 'px-4')}>

        {/* Live indicator + user name */}
        <div className={clsx('flex items-center gap-2 text-[11px] text-white/40 mb-2', !showLabels && 'justify-center')}>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
          {showLabels && <span className="sidebar-label tabular-nums">{t('nav.live')}</span>}
        </div>
        {showLabels && user && (
          <div className="sidebar-label text-[11px] text-white/35 truncate mb-2.5">{user.name ?? user.username}</div>
        )}

        {/* Help — Centro de ayuda */}
        <div className={clsx('mb-2.5', !showLabels && 'flex justify-center')}>
          <button
            onClick={() => { setHelpOpen(true); if (isMobile) close() }}
            title={!showLabels ? t('nav.help') : undefined}
            className={clsx(
              'flex items-center rounded-lg border border-white/10 bg-white/5 text-white/60',
              'hover:bg-white/10 hover:text-white/90 active:bg-white/15 transition-colors cursor-pointer',
              !showLabels ? 'justify-center p-1.5' : 'w-full justify-center gap-2 py-2 text-[11px] font-medium',
            )}
          >
            <IconHelp />
            {showLabels && <span className="sidebar-label">{t('nav.help')}</span>}
          </button>
        </div>

        {/* Logout + dark mode toggle */}
        <div className={clsx('flex items-center gap-3', !showLabels ? 'justify-center' : 'justify-between')}>
          {!showLabels ? (
            <>
              <button
                onClick={logout}
                title={t('nav.logout')}
                className="text-white/30 hover:text-white/70 transition-colors p-1 cursor-pointer"
              >
                <IconLogout />
              </button>
              <button
                onClick={() => setDark(!dark)}
                title={dark ? t('nav.lightMode') : t('nav.darkMode')}
                className="text-white/30 hover:text-white/70 transition-colors p-1 cursor-pointer"
              >
                {dark ? (
                  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <circle cx="8" cy="8" r="3.5"/>
                    <path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M3.1 3.1l1.1 1.1M11.8 11.8l1.1 1.1M3.1 12.9l1.1-1.1M11.8 4.2l1.1-1.1"/>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M13.5 10A6 6 0 016 2.5a6 6 0 000 11 6 6 0 007.5-3.5z"/>
                  </svg>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={logout}
                className="text-[11px] text-white/35 hover:text-white/70 transition-colors cursor-pointer"
              >
                {t('nav.logout')}
              </button>
              <button
                onClick={() => setDark(!dark)}
                title={dark ? t('nav.lightMode') : t('nav.darkMode')}
                className="text-[11px] text-white/35 hover:text-white/70 transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                {dark ? (
                  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <circle cx="8" cy="8" r="3.5"/>
                    <path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M3.1 3.1l1.1 1.1M11.8 11.8l1.1 1.1M3.1 12.9l1.1-1.1M11.8 4.2l1.1-1.1"/>
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M13.5 10A6 6 0 016 2.5a6 6 0 000 11 6 6 0 007.5-3.5z"/>
                  </svg>
                )}
                {dark ? t('nav.lightMode') : t('nav.darkMode')}
              </button>
            </>
          )}
        </div>

        {/* ── Language selector ─────────────────────────────────────────────
            Expandido: fila de botones ES / EN
            Colapsado: sólo el idioma activo + dropdown flotante (opción B)
        ─────────────────────────────────────────────────────────────────── */}
        <div className={clsx('mt-3', showLabels && 'border-t border-white/8 pt-3')}>
          {showLabels ? (
            /* ── Modo expandido ── */
            <>
              <div className="sidebar-label text-[9px] font-bold uppercase tracking-widest text-white/30 mb-1.5 select-none">
                {t('lang.label')}
              </div>
              <div className="flex gap-1">
                {LANGS.map(lng => (
                  <button
                    key={lng}
                    onClick={() => i18n.changeLanguage(lng)}
                    title={lng.toUpperCase()}
                    className={clsx(
                      'text-[10px] font-bold uppercase px-2 py-1 rounded-md transition-colors cursor-pointer',
                      i18n.resolvedLanguage === lng
                        ? 'bg-white/20 text-white shadow-sm ring-1 ring-white/20'
                        : 'text-white/35 hover:text-white/70 hover:bg-white/10',
                    )}
                  >
                    {lng}
                  </button>
                ))}
              </div>
            </>
          ) : (
            /* ── Modo colapsado: botón activo → dropdown flotante ── */
            <div className="relative flex justify-center mt-2">
              {/* Overlay para cerrar al clicar fuera */}
              {langOpen && (
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setLangOpen(false)}
                  aria-hidden="true"
                />
              )}

              {/* Botón del idioma activo */}
              <button
                onClick={() => setLangOpen(prev => !prev)}
                title={t('lang.label')}
                className="text-[10px] font-bold uppercase px-2 py-1 rounded-md bg-white/20 text-white ring-1 ring-white/20 cursor-pointer hover:bg-white/30 transition-colors z-50 relative"
              >
                {i18n.resolvedLanguage ?? 'es'}
              </button>

              {/* Dropdown flotante con los idiomas */}
              {langOpen && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-slate-800 border border-white/15 rounded-xl py-1 shadow-2xl z-50 min-w-[64px]">
                  {LANGS.map(lng => (
                    <button
                      key={lng}
                      onClick={() => { i18n.changeLanguage(lng); setLangOpen(false) }}
                      className={clsx(
                        'flex w-full items-center justify-center py-1.5 px-3 text-[11px] font-bold uppercase transition-colors cursor-pointer',
                        i18n.resolvedLanguage === lng
                          ? 'text-white bg-white/12 rounded-lg mx-1 w-[calc(100%-8px)]'
                          : 'text-white/55 hover:text-white hover:bg-white/8',
                      )}
                    >
                      {lng}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Collapse toggle — solo desktop ───────────────────────────────────
          Fija el menú abierto (empuja el contenido) o lo vuelve al modo hover.
          Al colapsar se cierra en el acto aunque el ratón siga encima: es la
          respuesta visible al clic. */}
      {!isMobile && (
        <button
          onClick={() => {
            clearTimeout(hoverTimer.current)
            setHovered(false)
            setSidebarCollapsed(!collapsed)
          }}
          title={collapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
          className="shrink-0 flex items-center justify-center py-2.5 border-t border-white/10 text-white/30 hover:text-white/75 hover:bg-white/5 transition-colors cursor-pointer"
        >
          {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
        </button>
      )}

      {/* ── Centro de ayuda (portal a body) ──────────────────────────────── */}
      {helpOpen && <HelpCenterPanel dark={dark} onClose={() => setHelpOpen(false)} />}
    </aside>
  )

  if (isMobile) return aside
  // En escritorio el hueco en el flujo es el ancho FIJADO; el aside va encima
  // y puede ser más ancho que él mientras el ratón está dentro.
  return (
    <div className={clsx('relative shrink-0 transition-[width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none', collapsed ? 'w-[64px]' : 'w-[200px]')}>
      {aside}
    </div>
  )
}
